import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, User, LogOut, ShieldCheck, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { Button } from './ui/button';
import { getCurrentTheme, applyTheme } from '../utils/themeConfig';

const HeaderLogo = () => (
  <div className="flex items-center gap-3 sm:gap-4">
    <img
      src="/symbol.png"
      alt="Panchami symbol"
      className="h-12 w-12 shrink-0 object-contain mix-blend-lighten drop-shadow-[0_6px_16px_rgba(12,4,2,0.35)] sm:h-14 sm:w-14"
    />
    <div className="flex flex-col leading-none">
      <span className="font-heading text-[2rem] tracking-[0.08em] text-[#f0c26b] sm:text-[2.4rem]">
        Panchami
      </span>
      <span className="pl-1 text-[0.62rem] uppercase tracking-[0.42em] text-[#f6ead0] sm:text-[0.68rem]">
        Jewellery
      </span>
    </div>
  </div>
);

const Header = () => {
  const { user, logout } = useAuth();
  const { currency, setCurrency, supportedCurrencies } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTheme, setCurrentTheme] = useState(null);

  const initializeTheme = useCallback(() => {
    const theme = getCurrentTheme();
    setCurrentTheme(theme);
    applyTheme(theme);
  }, []);

  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleAboutNavigation = useCallback((event) => {
    if (location.pathname === '/about') {
      event.preventDefault();
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      return;
    }

    navigate('/about');
  }, [location.pathname, navigate]);

  const actionLinkClassName =
    'inline-flex items-center gap-2 rounded-sm px-3 py-2 text-sm font-medium text-[#f6e4c3] transition-colors hover:bg-[#5d2814]';

  return (
    <header className="sticky top-0 z-50 border-b border-[#c3873d]/45 bg-[radial-gradient(circle_at_50%_45%,rgba(124,55,18,0.38),transparent_42%),linear-gradient(180deg,rgba(34,10,6,0.98),rgba(66,24,10,0.96)_48%,rgba(49,17,8,0.98))] shadow-[0_14px_30px_rgba(24,8,4,0.28)] backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <HeaderLogo />
          </Link>

          {currentTheme?.isSpecial && (
            <div className="hidden md:flex items-center space-x-2 px-4 py-2 rounded-full" style={{ backgroundColor: currentTheme.primary + '15' }}>
              <Sparkles className="h-4 w-4" style={{ color: currentTheme.primary }} />
              <span className="text-sm font-medium" style={{ color: currentTheme.primary }}>
                {currentTheme.name} Special
              </span>
            </div>
          )}

          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className="font-medium text-[#f8e7c9] transition-colors hover:text-[#ffbf5b]"
              data-testid="home-nav-link"
            >
              Home
            </Link>
            <Link
              to="/products"
              className="font-medium text-[#f8e7c9] transition-colors hover:text-[#ffbf5b]"
              data-testid="products-nav-link"
            >
              Collections
            </Link>
            <Link
              to="/about"
              className="font-medium text-[#f8e7c9] transition-colors hover:text-[#ffbf5b]"
              data-testid="about-nav-link"
              onClick={handleAboutNavigation}
            >
              About Us
            </Link>
          </nav>

          <div className="ml-auto flex items-center space-x-2">
            {user ? (
              <>
                <Link to="/profile" className={actionLinkClassName} data-testid="profile-link">
                  <User className="h-7 w-7" style={{ color: 'var(--primary)' }} />
                  <span className="hidden sm:inline">Profile</span>
                </Link>
                <Link to="/wishlist" className={actionLinkClassName} data-testid="wishlist-link">
                  <Heart className="h-4 w-4" style={{ color: 'var(--primary)' }} />
                  <span className="hidden sm:inline">Wishlist</span>
                </Link>
                <Link to="/cart" className={actionLinkClassName} data-testid="cart-link">
                  <ShoppingCart className="h-4 w-4" style={{ color: 'var(--primary)' }} />
                  <span className="hidden sm:inline">Cart</span>
                </Link>
                {user.role === 'admin' && (
                  <Link to="/admin" data-testid="admin-link">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="transition-colors hover:bg-[#5d2814] text-[#f6e4c3]"
                    >
                      <ShieldCheck className="h-5 w-5" />
                    </Button>
                  </Link>
                )}
                <Button
                  variant="ghost"
                  className="gap-2 text-[#f6e4c3] transition-colors hover:bg-[#5d2814]"
                  onClick={handleLogout}
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </>
            ) : (
              <Link to="/login" data-testid="login-link">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 text-[#f6e4c3] transition-colors hover:bg-[#5d2814]"
                >
                  <User className="h-8 w-8" />
                </Button>
              </Link>
            )}
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="h-7 w-[58px] rounded-sm border border-[#b86f36]/70 bg-[rgba(71,27,12,0.62)] px-1 text-[10px] text-[#f8e7c9] focus:outline-none sm:h-8 sm:w-[64px] sm:px-1.5 sm:text-[11px]"
              data-testid="currency-selector"
              aria-label="Currency selector"
            >
              {supportedCurrencies.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
